import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getFieldComponent } from '../FieldTypes/registry';
import { evaluateLiveValidation } from '../validator';
import { FIELD_TYPE } from '../constants';
import {
  KNOWN_ROLE_LABELS,
  ADDRESS_WATERMARK_BY_COUNTRY,
} from './constants';
import { testId } from '../../../utils/testIds';

function capitalize(word) {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function splitAlphaNumeric(word) {
  const m = /^([a-zA-Z]+)([0-9]+)$/.exec(word);
  if (m) return `${capitalize(m[1])} ${m[2]}`;
  return null;
}

function deriveRoleLabel(field) {
  const key = field.fieldKey || '';
  const parts = key.split('_').filter(Boolean);
  const role = parts.length >= 2 ? parts[1].toLowerCase() : '';

  if (!role) return field.label || '';
  if (KNOWN_ROLE_LABELS[role]) return KNOWN_ROLE_LABELS[role];
  return splitAlphaNumeric(role) || capitalize(role);
}

function derivePlaceholder(groupLabel, roleLabel) {
  const prefix = String(groupLabel || '').trim().split(' ')[0] || '';
  return [prefix, roleLabel].filter(Boolean).join(' ');
}

function CompositeField({
  groupLabel,
  fields,
  derivedState,
  renderMode,
  renderType,
  description,
}) {
  const { control, watch, getValues, setError, clearErrors, formState: { errors } } = useFormContext();

  const countryField = fields.find(f => f.fieldType === FIELD_TYPE.COUNTRY);
  const countryKey = countryField?.fieldKey ?? null;
  const countryValue = countryKey ? watch(countryKey) : undefined;

  const anyVisible = fields.some(f => derivedState[f.fieldKey]?.visible !== false);
  if (!anyVisible) return null;

  const anyRequired = fields.some(f => derivedState[f.fieldKey]?.required);

  return (
    <div
      className="formGroup field-engine-composite"
      data-test={testId("field-group", groupLabel)}
    >
      <div className="wth-2">
        <label>
          {groupLabel}
          {anyRequired && <sup>*</sup>}
        </label>
      </div>

      <div className="wth-9">
        <div className="field-engine-composite-grid">
          {fields.map(field => {
            const fs = derivedState[field.fieldKey];
            if (!fs || fs.visible === false) return null;

            const FieldComponent = getFieldComponent(field.fieldType);
            if (!FieldComponent) return null;

            const roleLabel = deriveRoleLabel(field);
            const watermark = countryValue
              ? ADDRESS_WATERMARK_BY_COUNTRY[countryValue]?.[roleLabel]
              : null;
            const placeholder = (watermark && countryValue !== 'US')
              ? watermark
              : derivePlaceholder(groupLabel, watermark ?? roleLabel);
            const err = errors[field.fieldKey];
            const isMulti = field.fieldType === FIELD_TYPE.MULTIPLE_CHOICE_LIST;

            const subConfig = {
              ...field,
              fieldSettings: {
                ...(field.fieldSettings || {}),
                placeholder,
              },
            };

            return (
              <div
                key={field.fieldKey}
                className="field-engine-composite-cell"
                data-role={roleLabel}
                data-test={testId("field", field.fieldKey)}
              >
                <Controller
                  name={field.fieldKey}
                  control={control}
                  render={({ field: rhf }) => (
                    <FieldComponent
                      config={subConfig}
                      value={rhf.value}
                      onChange={(val) => {
                        rhf.onChange(val);
                        const liveMessage = evaluateLiveValidation(field, val, getValues(), fs);
                        if (liveMessage) {
                          setError(field.fieldKey, { type: 'live', message: liveMessage });
                        } else {
                          clearErrors(field.fieldKey);
                        }
                      }}
                      onBlur={rhf.onBlur}
                      disabled={fs.disabled}
                      required={fs.required}
                      options={fs.options}
                      error={err?.message ?? null}
                      renderMode={renderMode}
                      renderType={renderType}
                      multi={isMulti}
                    />
                  )}
                />
                {err?.message && (
                  <span className="field-engine-sub-error" role="alert">
                    {err.message}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {description && (
          <p className="formHelperText">{description}</p>
        )}
      </div>
    </div>
  );
}

export default CompositeField;
